import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { migrateLocalStorageToDatabase } from '../utils/migrateLocalStorageToDb';
import { Database, ArrowRight, CheckCircle, XCircle, AlertCircle } from 'lucide-react';

const MigratePage = () => {
  const navigate = useNavigate();
  const [migrating, setMigrating] = useState(false);
  const [result, setResult] = useState(null);

  const handleMigrate = async () => {
    setMigrating(true);
    setResult(null);
    
    try {
      const migrationResult = await migrateLocalStorageToDatabase();
      setResult(migrationResult);
    } catch (error) {
      setResult({
        success: false,
        message: error.message
      });
    } finally {
      setMigrating(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 p-8">
      <div className="max-w-2xl mx-auto">
        <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <Database className="w-16 h-16 text-blue-400 mx-auto mb-4" />
            <h1 className="text-3xl font-bold text-white mb-2">
              Migrate Assessments
            </h1>
            <p className="text-slate-400">
              Transfer your assessments from browser storage to the database
            </p>
          </div>

          {/* Info Box */}
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mb-6">
            <div className="flex gap-3">
              <AlertCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-200">
                <p className="font-medium mb-1">What does this do?</p>
                <p>
                  This will copy all assessments stored in your browser's localStorage
                  to the permanent database. Your original data will remain in localStorage
                  as a backup.
                </p>
              </div>
            </div>
          </div>

          {/* Migration Button */}
          {!result && (
            <button
              onClick={handleMigrate}
              disabled={migrating}
              className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-700 text-white rounded-lg font-medium transition-colors disabled:cursor-not-allowed"
            >
              {migrating ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>Migrating...</span>
                </>
              ) : (
                <>
                  <Database className="w-5 h-5" />
                  <span>Start Migration</span>
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
          )}

          {/* Results */}
          {result && (
            <div className="space-y-4">
              {result.success ? (
                <>
                  {/* Success Summary */}
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <CheckCircle className="w-6 h-6 text-green-400" />
                      <h3 className="text-lg font-semibold text-white">
                        Migration Complete!
                      </h3>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <p className="text-2xl font-bold text-green-400">
                          {result.migrated}
                        </p>
                        <p className="text-sm text-slate-400">Migrated</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-red-400">
                          {result.failed}
                        </p>
                        <p className="text-sm text-slate-400">Failed</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-blue-400">
                          {result.total}
                        </p>
                        <p className="text-sm text-slate-400">Total</p>
                      </div>
                    </div>
                  </div>

                  {/* Errors */}
                  {result.errors && result.errors.length > 0 && (
                    <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <XCircle className="w-5 h-5 text-red-400" />
                        <h4 className="font-medium text-white">Errors:</h4>
                      </div>
                      <div className="space-y-1">
                        {result.errors.map((err, idx) => (
                          <p key={idx} className="text-sm text-red-200">
                            • {err.id}: {err.error}
                          </p>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-3">
                    <button
                      onClick={() => navigate('/user-dashboard')}
                      className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
                    >
                      Go to Dashboard
                    </button>
                    <button
                      onClick={() => window.location.reload()}
                      className="flex-1 px-4 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-medium transition-colors"
                    >
                      Migrate Again
                    </button>
                  </div>
                </>
              ) : (
                <>
                  {/* Error */}
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-6">
                    <div className="flex items-center gap-3 mb-2">
                      <XCircle className="w-6 h-6 text-red-400" />
                      <h3 className="text-lg font-semibold text-white">
                        Migration Failed
                      </h3>
                    </div>
                    <p className="text-red-200">{result.message}</p>
                  </div>

                  <button
                    onClick={() => window.location.reload()}
                    className="w-full px-4 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-medium transition-colors"
                  >
                    Try Again
                  </button>
                </>
              )}
            </div>
          )}

          {/* Back Link */}
          <div className="mt-6 text-center">
            <button
              onClick={() => navigate('/user-dashboard')}
              className="text-slate-400 hover:text-slate-300 text-sm transition-colors"
            >
              ← Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MigratePage;
